import './App.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth, ROLE_ADMIN, ROLE_OWNER, roleIdSetForUser } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Rooms from './pages/Rooms/Rooms';
import Home from './pages/Home/Home';
import Terms from './pages/Terms/Terms';
import Contact from './pages/Contact/Contact';
import Login from './pages/Login/Login';
import Signup from './pages/Signup/Signup';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import SetPassword from './pages/SetPassword/SetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import PropertyDetail from './pages/Dashboard/PropertyDetail';
import FrontDesk from './pages/FrontDesk/FrontDesk';
import ApartmentDetail from './pages/ApartmentDetail/ApartmentDetail';
import BookApartment from './pages/BookApartment/BookApartment';
import MyBookings from './pages/MyBookings/MyBookings';
import Profile from './pages/Profile/Profile';
import WhatsAppFloat from './component/WhatsAppFloat/WhatsAppFloat';
import BackToTop from './component/BackToTop/BackToTop';
import PdfViewerHost from './components/PdfViewerHost';

/** Owners use Front Desk; keep /dashboard for admins only (bookmarks still hit legacy routes). */
function LegacyOwnerDashboardRedirect({ children }) {
  const { user } = useAuth();
  const ids = roleIdSetForUser(user);
  const isAdmin = ids.has(ROLE_ADMIN) || user?.role === 'Admin';
  const isOwner = ids.has(ROLE_OWNER) || user?.role === 'Owner';
  if (user && isOwner && !isAdmin) {
    return <Navigate to="/front-desk" replace />;
  }
  return children;
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
        <div className="App">
          <PdfViewerHost />
          <BackToTop />
          <WhatsAppFloat />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/apartments/:apartmentId" element={<ApartmentDetail />} />
            <Route path="/book/:apartmentId" element={<BookApartment />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/my-bookings" element={<MyBookings />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute requireAccountProfileAbility>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireOwner>
                  <LegacyOwnerDashboardRedirect>
                    <Dashboard />
                  </LegacyOwnerDashboardRedirect>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/properties/:propertyId"
              element={
                <ProtectedRoute requireOwner>
                  <LegacyOwnerDashboardRedirect>
                    <PropertyDetail />
                  </LegacyOwnerDashboardRedirect>
                </ProtectedRoute>
              }
            />
            <Route
              path="/front-desk"
              element={
                <ProtectedRoute requireFrontDeskStaff>
                  <FrontDesk />
                </ProtectedRoute>
              }
            />
            {/* Legacy booking URLs redirect to rooms */}
            <Route path="/abuja-booking" element={<Navigate to="/rooms" replace />} />
            <Route path="/enugu-booking" element={<Navigate to="/rooms" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
